// TODO: import { ToastProgrammatic as Toast } from 'buefy'
import moment from 'moment'

import { Building } from '@/apps/brelag/common/models/building'
import { Customer } from '@/apps/brelag/common/models/customer'
import {
  INT_TO_NETMASK_MAP,
  parseIp,
} from '@/apps/brelag/common/models/eGateConfig'
import {
  BrelagConfigKey,
  DeviceAssignment,
  EquipmentCategory,
} from '@/apps/brelag/common/models/equipment'
import { Link } from '@/apps/brelag/common/models/link'
import {
  FloorDump,
  ProjectEditor,
  ProjectState,
} from '@/apps/brelag/mandator-user/project-editor/projectEditor'
import { downloadBlob } from '@/util/download'

const NEWLINE = '\r\n'
const EXPORT_VERSION = '1.00'

interface eGateConnectedLink {
  index: number
  storageId: number
  sender: string
  channel: number
  prettyChannel: number
  configData: string
}

export async function downloadEGateConfig(
  projectEditor: ProjectEditor,
  eGateId: string,
  showSnackbar: (message: string) => void
) {
  const config = await exportConfig(projectEditor, eGateId, showSnackbar)
  const blob = new Blob([config], {
    type: 'text/plain;charset=utf-8',
  })
  const filename = `${
    projectEditor.project.title
  }_v8_Export_eGate_${moment().format('DD.MM.YYYY')}.txt`
  downloadBlob(blob, filename)
}

export async function exportConfig(
  projectEditor: ProjectEditor,
  eGateId: string,
  showSnackbar,
  showWarnings: boolean = true
): Promise<string> {
  const eGate = await projectEditor.apiClient.get<DeviceAssignment>(
    DeviceAssignment,
    eGateId
  )

  if (!eGate.radio_address && showWarnings) {
    showSnackbar(`eGate noch nicht eingelernt. Config wird leer gelassen.`)
  }

  const project: ProjectState = projectEditor.project
  const customer: Customer = projectEditor.customer
  const senders = projectEditor.deviceHandler.senderList
  const receivers = projectEditor.deviceHandler.receiverList
  const links = projectEditor.deviceHandler.links

  const i = 1
  const _connectedLinks = projectEditor.deviceHandler.findLinks(
    undefined,
    eGate.id
  )
  const connectedLinks: eGateConnectedLink[] = []
  let senderNoRadioAddressToastShown = false
  for (const link of _connectedLinks) {
    const sender = projectEditor.deviceHandler.getDeviceAssignmentById(
      link.sender,
      EquipmentCategory.SENDER,
      false
    )

    const storageId = link.options.keyStorageId
    const designator = link.options.keyDesignator
    let configData = ''
    if (
      !senderNoRadioAddressToastShown &&
      !sender.radio_address &&
      showWarnings
    ) {
      showSnackbar(`Noch nicht alle Sender eingelernt. Config unvollständig.`)
      // Only show once
      senderNoRadioAddressToastShown = true
    }
    if (eGate.radio_address && sender.radio_address) {
      const configDataResponse = await projectEditor.apiClient.customGet(
        'brelag/egate/config',
        {
          receiver_address: eGate.radio_address,
          sender_address: sender.radio_address,
          channel: link.sender_channel,
          designator,
          index: storageId,
        }
      )
      configData = configDataResponse.data
    }
    connectedLinks.push({
      index: i,
      storageId: storageId,
      sender: sender.device_number,
      channel: link.sender_channel,
      prettyChannel: link.sender_channel + 1,
      configData,
    })
  }

  const output = new eGateExporter(
    eGate,
    senders,
    receivers,
    links,
    connectedLinks,
    project,
    customer,
    projectEditor
  ).output

  return output
}

class eGateExporter {
  output = ''
  eGate: DeviceAssignment
  project: ProjectState
  senders: DeviceAssignment[] = []
  receivers: DeviceAssignment[] = []
  links: Link[] = []
  connectedLinks: eGateConnectedLink[]
  customer: Customer
  config: {
    [key: string]: any
  } = null
  projectEditor: ProjectEditor
  senderIndexMap: Map<string, number> = new Map()
  receiverIndexMap: Map<string, number> = new Map()

  constructor(
    eGate: DeviceAssignment,
    senders: DeviceAssignment[],
    receivers: DeviceAssignment[],
    links: Link[],
    connectedLinks: eGateConnectedLink[],
    project: ProjectState,
    customer: Customer,
    projectEditor: ProjectEditor
  ) {
    this.projectEditor = projectEditor
    this.eGate = eGate
    this.senders = senders
    this.receivers = receivers
    this.links = links
    this.connectedLinks = connectedLinks
    this.config = this.eGate.config.egate
    this.project = project
    this.customer = customer
    this.writeInfo()
      .writeProject()
      .writeCustomer()
      .writeCreator()
      .writeTransmitter()
      .writeReceiver()
      .writeLink()
      .writeEGate()
  }

  writeInfo() {
    return this.insertHeader('Info')
      .insertKeyValue('Version', EXPORT_VERSION)
      .insertKeyValue('Date', moment().format('DD.MM.YYYY'))
  }

  writeProject() {
    return this.insertHeader('Project')
      .insertKeyValue('Name', this.project.title)
      .insertKeyValue('Address', this.project.meta.address)
      .insertKeyValue('Town', this.project.meta.city)
  }

  writeCustomer() {
    const companyName = this.customer.name
      ? this.customer.name
      : this.customer.number
    return this.insertHeader('Customer')
      .insertKeyValue('Company', companyName)
      .insertKeyValue('Contact', this.customer.meta.contact_person)
      .insertKeyValue('Address', this.customer.meta.address)
      .insertKeyValue('Town', this.customer.meta.city)
      .insertKeyValue('Phone_Company', this.customer.meta.tel_central)
      .insertKeyValue('Phone_Contact', this.customer.meta.tel_direct)
      .insertKeyValue('Phone_Mobile', this.customer.meta.tel_mobile)
      .insertKeyValue('Mail', this.customer.meta.email)
  }

  writeCreator() {
    return this.insertHeader('Creator')
      .insertKeyValue('Planner', this.project.meta.plan_drawer)
      .insertKeyValue('Programmer', this.project.meta.programmer)
  }

  writeTransmitter() {
    this.insertHeader('Transmitter').insertLine(
      `//Index=Number;Type;Version;Name;Location\\Sublocation;`
    )
    let i = 1
    for (const sender of this.senders) {
      const floor: FloorDump = this.projectEditor.floorsMap.get(sender.floor)
      const building: Building = this.projectEditor.buildingsMap.get(
        floor.building
      )

      const equip = this.projectEditor.equipmentHandler.getVariantFromId(
        sender.equipment_variant
      )
      this.insertLine(
        `${i}=${sender.device_number.slice(3)};${equip.device_variant_name};${
          sender.programmer_device_info &&
          sender.programmer_device_info.SwVersion
            ? DeviceAssignment.prettySwVersion(
                sender.programmer_device_info.SwVersion
              )
            : ''
        };;${building.title}\\${floor.title};`
      )
      this.senderIndexMap.set(sender.device_number, i)
      i += 1
    }
    return this
  }

  writeReceiver() {
    this.insertHeader('Receiver').insertLine(
      '//Index=Number;Type;Version;Name;Option1=,Option2=,...'
    )
    let i = 1
    for (const receiver of this.receivers) {
      let receiverOptions = ''
      // Filter out invalid keys
      const equipment =
        this.projectEditor.equipmentHandler.getEquipmentFromDevice(receiver)
      const internalConfig = DeviceAssignment.getInternalConfig(
        receiver.config,
        equipment.config_editor_layout
      )

      for (const key of Object.keys(internalConfig)) {
        if (key === BrelagConfigKey.EGATE) {
          receiverOptions += `dhcp=${internalConfig[key].dhcp},`
          receiverOptions += `ip=${parseIp(internalConfig[key].ip)},`
          receiverOptions += `gateway=${parseIp(internalConfig[key].gateway)},`
          receiverOptions += `dns=${parseIp(internalConfig[key].dns)},`
          receiverOptions += `port=${parseInt(internalConfig[key].port)},`
        } else {
          // This should not happen, but double check
          let value = internalConfig[key]
          if (value === true || value === 'true') {
            value = 1
          } else if (value === false || value === 'false') {
            value = 0
          } else {
            value = parseInt(value)
          }
          receiverOptions += `${key}=${value},`
        }
      }
      const variant = this.projectEditor.equipmentHandler.getVariantFromId(
        receiver.equipment_variant
      )
      // TODO: Maybe add ${getDeviceRemark(receiver)} as "name" ?
      this.insertLine(
        `${i}=${receiver.device_number.slice(3)};${
          variant.device_variant_name
        };${
          receiver.programmer_device_info &&
          receiver.programmer_device_info.SwVersion
            ? DeviceAssignment.prettySwVersion(
                receiver.programmer_device_info.SwVersion
              )
            : ''
        };;${receiverOptions}`
      )
      this.receiverIndexMap.set(receiver.device_number, i)
      i += 1
    }
    return this
  }

  writeLink() {
    this.insertHeader('Link').insertLine(
      '//Index=TransmitterIndex;Channel;ReceiverIndex;Option1=,Option2=,...'
    )
    let i = 1
    for (const link of this.links) {
      let linkOptions = ''
      const sender = this.projectEditor.deviceHandler.getDeviceAssignmentById(
        link.sender,
        EquipmentCategory.SENDER,
        false
      )
      const receiver = this.projectEditor.deviceHandler.getDeviceAssignmentById(
        link.receiver,
        EquipmentCategory.RECEIVER,
        false
      )
      const equipment =
        this.projectEditor.equipmentHandler.getEquipmentFromDevice(receiver)
      const teachingOptionsConfig = DeviceAssignment.getInternalConfig(
        link.options,
        equipment.teaching_options
      )
      for (const key of Object.keys(teachingOptionsConfig)) {
        if (key !== 'configuration' && key !== 'groups') {
          // This should not happen, but double check
          let value = teachingOptionsConfig[key]
          if (value === true || value === 'true') {
            value = 1
          } else if (value === false || value === 'false') {
            value = 0
          } else {
            value = parseInt(value)
          }
          linkOptions += `${key}=${value},`
        }
      }

      this.insertLine(
        `${i}=${this.senderIndexMap.get(sender.device_number)};${
          link.sender_channel + 1
        };${this.receiverIndexMap.get(receiver.device_number)};${linkOptions}`
      )
      i += 1
    }
    return this
  }

  writeEGate() {
    const dhcp = this.config.dhcp ? 1 : 0
    const serial_number =
      this.eGate.programmer_device_info &&
      this.eGate.programmer_device_info.SerNr
        ? this.eGate.programmer_device_info.SerNr.toString()
        : ''
    this.insertHeader('eGate1')
      .insertKeyValue('SerialNr', serial_number)
      .insertKeyValue('DeviceNr', this.eGate.device_number.slice(3))
      .insertKeyValue('DHCP', dhcp.toString())
      .insertKeyValue('IP-Address', this.config.ip)
      .insertKeyValue('Gateway', this.config.gateway)
      .insertKeyValue('DNS', this.config.dns)
      .insertKeyValue('Port', this.config.port)
      .insertKeyValue('Netmask', INT_TO_NETMASK_MAP[this.config.netmask])
      .insertKeyValue('MAC-Address', '??????')
      .insertLine('//Index=ID;TransmitterIndex;Channel;ConfigData')

    let i = 1
    for (const link of this.connectedLinks) {
      this.insertLine(
        `${i}=${link.storageId};${this.senderIndexMap.get(link.sender)};${
          link.prettyChannel
        };${link.configData}`
      )
      i += 1
    }
    return this
  }

  insertKeyValue(key, value?) {
    if (!value) {
      value = ''
    }
    this.output += `${key}=${value}${NEWLINE}`
    return this
  }

  insertHeader(name) {
    this.output += `[${name}]${NEWLINE}`
    return this
  }

  insertLine(text: string) {
    this.output += `${text}${NEWLINE}`
    return this
  }
}
